import { useState } from 'react'

import { ProductWidget as F1ProductWidget } from '@factorialco/f0-react'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { ProductModal } from 'modules/upselling_kit/components/ProductModal'
import { useDismissalState } from 'modules/upselling_kit/hooks/useDismissalState'
import { useUpsell } from 'modules/upselling_kit/lib/upsellContext'
import {
  ProductWidgetAction,
  UpsellingKitModule,
  UpsellingKitProductWidget,
} from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import {
  handleUpsellingLearnMore,
  handleUpsellingOnClick,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

type BaseProps = Omit<UpsellingKitProductWidget, keyof UpsellingKitModule>

// Helper functions
const createUpsellAction = (
  action: ProductWidgetAction & { type: 'upsell' },
  config: {
    showModuleModal: boolean
    onClick?: () => void
    requestSource: string
    moduleRequested: AvailableModules
    featureRequested?: Features
    pageSource?: string
    setOpenUpsellModal: (open: boolean) => void
    defaultMessages: {
      error: { title: string; description: string }
      success: { title: string; description: string; buttonLabel: string }
      nextSteps: { title: string; items: Array<{ text: string; isCompleted?: boolean }> }
      loading: { label: string }
      close: string
    }
    requestReason: ValidReasons | undefined
  },
  upsellCreate: (props: {
    moduleRequested: AvailableModules
    requestSource: string
    requestReason: ValidReasons
    featureRequested?: Features
    pageSource?: string
    upsellRequestComponentType?: string
  }) => void
) => ({
  type: 'upsell' as const,
  label: action.label,
  variant: action.variant || 'promote',
  errorMessage: action.errorMessage || config.defaultMessages.error,
  successMessage: {
    ...config.defaultMessages.success,
    buttonOnClick: () => config.setOpenUpsellModal(false),
  },
  nextSteps: action.nextSteps || config.defaultMessages.nextSteps,
  loadingState: action.loadingState || config.defaultMessages.loading,
  closeLabel: action.closeLabel || config.defaultMessages.close,
  showConfirmation: action.showConfirmation ?? true,
  onClick: async () => {
    if (config.showModuleModal) {
      handleUpsellingOnClick({
        onClick: config.onClick,
        requestSource: config.requestSource,
        moduleRequested: config.moduleRequested,
        component: 'product_widget',
        setOpenUpsellModal: config.setOpenUpsellModal,
      })
    } else {
      action.onClick?.()
      await upsellCreate({
        moduleRequested: config.moduleRequested,
        requestSource: config.requestSource,
        requestReason: config.requestReason || ValidReasons.MissingAddon,
        featureRequested: config.featureRequested,
        pageSource: config.pageSource,
        upsellRequestComponentType: 'product_widget',
      })
    }
  },
})

const createRegularAction = (
  action: ProductWidgetAction & { type: 'regular' },
  config: {
    showModuleModal: boolean
    onClick?: () => void
    requestSource: string
    moduleRequested: AvailableModules
    setOpenUpsellModal: (open: boolean) => void
  }
) => ({
  type: 'regular' as const,
  variant: action.variant || 'outline',
  label: action.label,
  showConfirmation: action.showConfirmation ?? false,
  onClick: async () => {
    if (config.showModuleModal) {
      handleUpsellingOnClick({
        onClick: config.onClick,
        requestSource: config.requestSource,
        moduleRequested: config.moduleRequested,
        component: 'product_widget',
        setOpenUpsellModal: config.setOpenUpsellModal,
      })
    } else {
      action.onClick?.()
      handleUpsellingLearnMore({
        moduleRequested: config.moduleRequested,
        requestSource: config.requestSource,
        component: 'product_widget',
      })
    }
  },
})

const ProductWidgetContent = ({
  dismissible,
  title,
  description,
  mediaUrl,
  actions,
  showModuleModal = false,
  pageSource,
}: BaseProps) => {
  const {
    moduleRequested,
    requestSource,
    featureRequested,
    requestReason,
    isAllowed,
    upsellData,
    onClick,
    onClose,
  } = useUpsell()
  const [openUpsellModal, setOpenUpsellModal] = useState(false)
  const { isVisible, handleDismiss } = useDismissalState(moduleRequested, isAllowed, requestSource)
  const { create: upsellCreate } = useCreateUpsellRequest()
  const defaultMessages = useDefaultMessages({
    component: 'product_widget',
    requestSource,
  })

  const handleClose = () => {
    handleDismiss()
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'product_widget',
    })
  }

  if (!isVisible) return null

  const actionConfig = {
    showModuleModal,
    onClick,
    requestSource,
    moduleRequested,
    setOpenUpsellModal,
    defaultMessages,
    pageSource,
    requestReason,
  }

  const actionsMapped = actions.map((action) =>
    action.type === 'upsell'
      ? createUpsellAction(action, actionConfig, upsellCreate)
      : createRegularAction(action, actionConfig)
  )

  return (
    <>
      <F1ProductWidget
        mediaUrl={mediaUrl}
        title={title || upsellData.moduleName || ''}
        description={description || upsellData.moduleDescription || ''}
        onClose={handleClose}
        dismissible={dismissible ?? true}
        width="300px"
        trackVisibility={() =>
          handleUpsellingVisibility({
            moduleRequested,
            requestSource,
            component: 'product_widget',
          })
        }
        actions={actionsMapped}
      />
      {openUpsellModal && (
        <ProductModal
          moduleRequested={moduleRequested}
          requestReason={ValidReasons.MissingAddon}
          featureRequested={featureRequested}
          requestSource={requestSource}
          pageSource={requestSource}
          title={upsellData.upsellModalDescription || ''}
          isOpen={openUpsellModal}
          onClose={() => setOpenUpsellModal(false)}
          modalTitle={upsellData.moduleName || ''}
          upsellRequestComponentType="product_widget"
        />
      )}
    </>
  )
}

export default ProductWidgetContent
